import type {
  MutationCacheNotifyEvent,
  QueryCacheNotifyEvent,
  QueryClient,
} from "@tanstack/react-query";

import { logger as coreLogger } from "./logger";

/**
 * JSON.stringify wrapper that is resilient to circular references and
 * DOM/Function values that cannot be serialized. Falls back to placeholders.
 */
const safeStringify = (value: unknown): string => {
  try {
    const result = JSON.stringify(value, getCircularReplacer());
    if (typeof result === "string" && result !== undefined && result !== null) {
      return result;
    }
    // JSON.stringify on undefined, functions etc. returns undefined → convert to string
    return String(result ?? value);
  } catch {
    // Last-ditch fallback – at least prevent runtime crashes in logger
    return "[unserializable]";
  }
};

/**
 * Build a replacer that skips circular structures and replaces problematic
 * values (DOM nodes, functions, etc.) with small placeholder strings so the
 * log output remains informative but safe.
 */
const getCircularReplacer = () => {
  const seen = new WeakSet();
  return (_key: string, val: unknown) => {
    if (typeof val === "object" && val !== null) {
      if (seen.has(val)) {
        return "[Circular]";
      }
      seen.add(val);

      // Replace DOM nodes with a short description to avoid huge dumps and
      // circular refs originating from React fibers.
      if (typeof Element !== "undefined" && val instanceof Element) {
        return `[HTMLElement ${val.tagName}]`;
      }
    }

    if (typeof val === "function") {
      return `[Function ${val.name || "anonymous"}]`;
    }

    return val as unknown;
  };
};

/**
 * Utility: stringify and truncate to avoid flooding the console. Uses
 * safeStringify under the hood.
 */
const stringifyShort = (val: unknown, maxLen = 100): string => {
  const full = safeStringify(val);
  const str = typeof full === "string" ? full : String(full);
  return str.length > maxLen ? str.slice(0, maxLen) : str;
};

const isDev = import.meta.env.MODE === "development";
const rqSubLogger = coreLogger.getSubLogger({ name: "ReactQuery" });

/**
 * Attach compact cache listeners that pipe events through ts-log.
 */
export function attachReactQueryLogger(queryClient: QueryClient): void {
  if (!isDev) return;

  // helper → stringify key concisely
  const toKey = (k: unknown) => (Array.isArray(k) ? k.join("/") : String(k));

  const summarizeQuery = (e: QueryCacheNotifyEvent) => {
    if (e.type === "observerOptionsUpdated") return null;
    if (!e.query) return { type: e.type };
    const options = e.query.options as any;
    if (options && options.enabled === false) return null;
    const {
      queryKey,
      state: { status, fetchStatus },
    } = e.query;
    return { type: e.type, key: toKey(queryKey), status, fetchStatus };
  };

  const summarizeMutation = (e: MutationCacheNotifyEvent) => {
    if (e.type === "observerOptionsUpdated") return null;
    if (!e.mutation) return { type: e.type };
    const {
      state: { status, failureCount },
    } = e.mutation;
    return { type: e.type, status, failureCount };
  };

  queryClient.getQueryCache().subscribe((ev) => {
    const s = summarizeQuery(ev);
    // Only log errors and important events, not every cache update
    if (s && s.status === "error") {
      const { type, key, status, fetchStatus } = s;
      rqSubLogger.debug(`QC ${type.padEnd(22)} | ${status}/${fetchStatus} | ${key} `);
    }
  });

  queryClient.getMutationCache().subscribe((ev) => {
    const s = summarizeMutation(ev);
    // Only log errors and failures, not every mutation event
    if (s && (s.status === "error" || s.failureCount > 0)) {
      rqSubLogger.debug("MC %j", s);
    }
  });
}

export function initDevLoggers(queryClient?: QueryClient): void {
  if (!isDev) return;
  if (queryClient) attachReactQueryLogger(queryClient);
}

/**
 * Attach logging to all methods on an actions object.
 * Logs start, success, and errors for each action.
 * Options: include and/or exclude lists to filter which actions to log.
 */
export function attachActionLogger(
  actions: Record<string, (...args: unknown[]) => unknown>,
  name: string,
  options?: { include?: string[]; exclude?: string[] }
): void {
  if (!isDev) return;
  // Guard: actions must be a non-null plain object
  if (!actions || typeof actions !== "object" || Array.isArray(actions)) {
    coreLogger.warn(`[attachActionLogger] Skipped: actions is not a plain object`, { actions });
    return;
  }
  let entries: [string, unknown][];
  try {
    entries = Object.entries(actions);
  } catch (err) {
    coreLogger.error(`[attachActionLogger] Object.entries failed`, {
      error: (err as Error).message,
      actions,
    });
    return;
  }
  const subLogger = coreLogger.getSubLogger({ name });
  const includeSet = options?.include ? new Set(options.include) : null;
  const excludeSet = new Set(options?.exclude || []);
  for (const [key, fn] of entries) {
    if (includeSet && !includeSet.has(key)) continue;
    if (excludeSet.has(key)) continue;
    if (typeof fn !== "function") {
      subLogger.warn(`[attachActionLogger] Skipped: ${key} is not a function`, { value: fn });
      continue;
    }
    const original = fn.bind(actions);
    const wrappedFn = (...args: unknown[]) => {
      const argStr = stringifyShort(args);
      subLogger.debug(`${name}.${key} start args=${argStr}`);
      try {
        const result = original(...args);
        if (result && typeof result.then === "function") {
          return result
            .then((res: unknown) => {
              const resStr = stringifyShort(res);
              subLogger.debug(`${name}.${key} success result=${resStr}`);
              return res;
            })
            .catch((err: Error) => {
              subLogger.error(`${name}.${key} error ${err.message}`);
              throw err;
            });
        }
        const resultStr = stringifyShort(result);
        subLogger.debug(`${name}.${key} success result=${resultStr}`);
        return result;
      } catch (err: unknown) {
        subLogger.error(`${name}.${key} error ${(err as Error).message}`);
        throw err;
      }
    };
    actions[key] = wrappedFn as (...args: unknown[]) => unknown;
  }
}

/**
 * Lightweight logger object compatible with React-Query's `logger` option.
 */
export const createQueryLogger = () => {
  const log = (...args: unknown[]) => rqSubLogger.debug(...args);
  const warn = (...args: unknown[]) => rqSubLogger.warn(...args);
  const error = (...args: unknown[]) => rqSubLogger.error(...args);

  return { log, warn, error } as const;
};
